/**
 * The file viewer.
 *
 * Until now the only thing you could do with a file was download it, which for
 * a screenshot or a short note is a poor trade — you leave the app, open a
 * second program, and end up with a decrypted copy on disk you did not
 * particularly want.
 *
 * Nothing new is needed cryptographically. `decryptToBlob` already returns a
 * Blob tagged with the decrypted MIME type, and the CSP already allows `blob:`
 * for `img-src` and `media-src` precisely so a decrypted preview can render.
 * So this is a dispatcher on `node.mime` and an object URL.
 *
 * **No blob: iframe, and no PDF viewer yet.** A `blob:` document inherits the
 * origin that created it, so an iframe pointed at one is same-origin script
 * running on a page that is holding the master key in memory. That is not a
 * trade worth making for a file preview. A PDF needs a renderer that draws to a
 * canvas — pdf.js and a bundled worker — which is its own change; until then
 * PDFs offer the download they always did, and say why.
 *
 * Everything is buffered rather than streamed, matching `downloadToDisk` and
 * for the same reason: a chunk that fails its integrity check must fail the
 * whole file rather than render half of it as though it were fine.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ds } from '../ds';
import { ApiError } from '../api';
import { downloadToDisk, previewText, previewUrl, type DecryptedNode } from '../nodes';
import { fileIcon, formatBytes, monoPlain, SHADOW, tint } from '../theme';
import { useIsNarrow } from '../responsive';
import { Versions } from './Versions';
import { useT } from '../i18n';

/**
 * How much we are willing to hold in memory to show something.
 *
 * A preview buffers the whole plaintext, so this is a real ceiling rather than
 * a stylistic one — a 2 GB video would take the tab down. Above it the viewer
 * offers the download instead, which streams past the same problem.
 */
const MAX_PREVIEW_BYTES = 150 * 1024 * 1024;
/** Text is rendered into the DOM, so it gets a much tighter bound. */
const MAX_TEXT_BYTES = 2 * 1024 * 1024;

type Kind = 'image' | 'video' | 'audio' | 'text' | 'pdf' | 'none';

/**
 * What we can show for a MIME type.
 *
 * Deliberately keyed on the same taxonomy `theme.ts` uses for the type tint and
 * the row icon, so the icon on the row and the viewer behind it never disagree
 * about what a file is.
 */
export function previewKind(mime: string | null): Kind {
  if (!mime) return 'none';
  if (/^image\/(png|jpeg|webp|gif|avif|bmp|x-icon)$/.test(mime)) return 'image';
  // SVG is an image the browser will happily execute script inside. It renders
  // safely in an <img> — which does not run script — and nowhere else here.
  if (mime === 'image/svg+xml') return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  if (
    mime.startsWith('text/') ||
    /^application\/(json|xml|javascript|x-yaml|yaml|toml)$/.test(mime)
  ) {
    return 'text';
  }
  return 'none';
}

export const canPreview = (node: DecryptedNode): boolean =>
  node.kind === 'file' && previewKind(node.mime) !== 'none';

export function Preview({
  node,
  nodeKey,
  onClose,
  onRestored
}: {
  node: DecryptedNode;
  nodeKey: CryptoKey | null;
  onClose: () => void;
  /** Called when an older version is made current, so the caller can reload. */
  onRestored?: () => void;
}) {
  const t = useT();
  const { Button, Icon } = ds();
  const narrow = useIsNarrow();
  const kind = previewKind(node.mime);
  const [showVersions, setShowVersions] = useState(false);

  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Held in a ref as well as state so the cleanup revokes the current URL even
  // if the effect re-runs before React has committed the new state.
  const urlRef = useRef<string | null>(null);

  const tooBig =
    node.plainSizeBytes > (kind === 'text' ? MAX_TEXT_BYTES : MAX_PREVIEW_BYTES);

  useEffect(() => {
    if (!nodeKey || kind === 'none' || kind === 'pdf' || tooBig) return;
    let cancelled = false;

    void (async () => {
      try {
        if (kind === 'text') {
          const body = await previewText(node, nodeKey);
          if (!cancelled) setText(body);
          return;
        }
        const objectUrl = await previewUrl(node, nodeKey, (done, total) => {
          if (!cancelled) setProgress({ done, total });
        });
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        urlRef.current = objectUrl;
        setUrl(objectUrl);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Could not decrypt this file.'
        );
      }
    })();

    return () => {
      cancelled = true;
      // An object URL pins its blob for the life of the document, so a leaked
      // one keeps a whole decrypted file resident.
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [node, nodeKey, kind, tooBig]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = useCallback(async () => {
    if (nodeKey) await downloadToDisk(node, nodeKey);
  }, [node, nodeKey]);

  const colours = tint(node.mime);

  return (
    <div
      onClick={onClose}
      className="mv-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(40,28,45,.62)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 30,
        padding: narrow ? '12px 12px calc(12px + env(safe-area-inset-bottom, 0px))' : 32
      }}
    >
      <div
        role="dialog"
        aria-label={node.name}
        onClick={(e) => e.stopPropagation()}
        className="mv-pop"
        style={{
          width: '100%',
          maxWidth: 1040,
          maxHeight: 'min(92vh, 94dvh)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface-card)',
          borderRadius: 22,
          boxShadow: SHADOW.xl,
          overflow: 'hidden'
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: narrow ? '14px 16px' : '18px 22px',
            borderBottom: '1px solid var(--border-subtle)'
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              flex: '0 0 36px',
              borderRadius: 12,
              display: 'grid',
              placeItems: 'center',
              background: colours.bg,
              color: colours.fg
            }}
          >
            <Icon name={fileIcon(node.kind, node.mime)} size={18} />
          </span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--text-strong)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {node.name}
            </div>
            <div style={monoPlain}>
              {formatBytes(node.plainSizeBytes)}
              {node.mime ? ` · ${node.mime}` : ''} · decrypted in your browser
            </div>
          </div>

          <button
            aria-label={t('preview.previous-versions')}
            title={t('preview.previous-versions')}
            aria-pressed={showVersions}
            onClick={() => setShowVersions((v) => !v)}
            style={{
              width: 34,
              height: 34,
              flex: '0 0 34px',
              borderRadius: 11,
              border: 'none',
              background: showVersions ? 'var(--primary-soft)' : 'transparent',
              color: showVersions ? 'var(--primary-text)' : 'var(--text-muted)',
              cursor: 'pointer'
            }}
          >
            <Icon name="history" size={16} />
          </button>

          {!narrow && (
            <Button variant="secondary" size="sm" onClick={download}>
              {t('preview.download')}
            </Button>
          )}
          <button
            aria-label={t('preview.close')}
            onClick={onClose}
            style={{
              width: 34,
              height: 34,
              flex: '0 0 34px',
              borderRadius: 11,
              border: 'none',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer'
            }}
          >
            <Icon name="x" size={17} />
          </button>
        </header>

        {showVersions && (
          <div style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            <Versions
              node={node}
              nodeKey={nodeKey}
              // Restoring changes which bytes the current version points at, so
              // the folder listing and this viewer are both stale afterwards.
              onRestored={onRestored ?? onClose}
            />
          </div>
        )}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'grid',
            placeItems: 'center',
            background: kind === 'image' || kind === 'video' ? 'var(--surface-sunken)' : undefined,
            padding: kind === 'text' ? 0 : narrow ? 12 : 20
          }}
        >
          <Body
            kind={kind}
            node={node}
            url={url}
            text={text}
            error={error}
            progress={progress}
            tooBig={tooBig}
            locked={!nodeKey}
            onDownload={download}
          />
        </div>

        {narrow && (
          <footer style={{ padding: '12px 16px', borderTop: '1px solid var(--border-subtle)' }}>
            <Button variant="secondary" size="md" fullWidth onClick={download}>
              {t('preview.download')}
            </Button>
          </footer>
        )}
      </div>
    </div>
  );
}

function Body({
  kind,
  node,
  url,
  text,
  error,
  progress,
  tooBig,
  locked,
  onDownload
}: {
  kind: Kind;
  node: DecryptedNode;
  url: string | null;
  text: string | null;
  error: string | null;
  progress: { done: number; total: number } | null;
  tooBig: boolean;
  locked: boolean;
  onDownload: () => void;
}) {
  const t = useT();
  const { Button } = ds();

  if (locked) return <Note>{t('preview.this-files-key-is-not')}</Note>;
  if (error) return <Note tone="bad">{error}</Note>;

  if (kind === 'pdf') {
    return (
      <Note>
        <p style={{ margin: '0 0 12px' }}>
          {t('preview.pdfs-are-not-previewed-here')}
        </p>
        <Button variant="primary" size="md" onClick={onDownload}>
          {t('preview.download-instead')}
        </Button>
      </Note>
    );
  }

  if (kind === 'none') {
    return (
      <Note>
        <p style={{ margin: '0 0 12px' }}>{t('preview.there-is-no-viewer-for')}</p>
        <Button variant="primary" size="md" onClick={onDownload}>
          {t('preview.download')}
        </Button>
      </Note>
    );
  }

  if (tooBig) {
    return (
      <Note>
        <p style={{ margin: '0 0 12px' }}>
          At {formatBytes(node.plainSizeBytes)} this is too large to open in the page.
        </p>
        <Button variant="primary" size="md" onClick={onDownload}>
          {t('preview.download')}
        </Button>
      </Note>
    );
  }

  if (kind === 'text') {
    if (text === null) return <Note>{t('preview.decrypting')}</Note>;
    return (
      <pre
        style={{
          margin: 0,
          padding: '18px 22px',
          width: '100%',
          alignSelf: 'start',
          fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
          fontSize: 12.5,
          lineHeight: 1.7,
          color: 'var(--text-body)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
      >
        {text}
      </pre>
    );
  }

  if (!url) {
    return (
      <Note>
        Decrypting
        {progress && progress.total > 1 ? ` · ${progress.done} of ${progress.total} chunks` : '…'}
      </Note>
    );
  }

  if (kind === 'image') {
    return (
      <img
        src={url}
        alt={node.name}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
      />
    );
  }

  if (kind === 'video') {
    return (
      <video
        src={url}
        controls
        style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', borderRadius: 12 }}
      />
    );
  }

  return <audio src={url} controls style={{ width: '100%', maxWidth: 520 }} />;
}

const Note = ({ children, tone }: { children: React.ReactNode; tone?: 'bad' }) => (
  <div
    style={{
      padding: '32px 28px',
      maxWidth: 460,
      textAlign: 'center',
      fontSize: 13.5,
      lineHeight: 1.6,
      color: tone === 'bad' ? 'var(--danger-600)' : 'var(--text-muted)'
    }}
  >
    {children}
  </div>
);
